import { useState } from 'react';
import type { FormEvent } from 'react';

import { fetchSiteProfile, submitLead } from '../api/client';
import PageHero from '../components/PageHero';
import { useApi } from '../hooks/useApi';
import { usePageMeta } from '../hooks/usePageMeta';
import { useT } from '../i18n/LanguageProvider';
import type { Dictionary } from '../i18n/translations';
import type { LeadRequest } from '../types/content';

type Field = keyof LeadRequest;
type Errors = Partial<Record<Field, string>>;
type Status = 'idle' | 'submitting' | 'sent' | 'failed';

function emptyLead(topic: string): LeadRequest {
  return {
    name: '',
    company: '',
    role: '',
    email: '',
    phone: '',
    topic,
    message: '',
    companyWebsite: ''
  };
}

const REQUIRED: Field[] = ['name', 'company', 'role', 'email', 'topic', 'message'];
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(values: LeadRequest, copy: Dictionary['contact']): Errors {
  const errors: Errors = {};

  for (const field of REQUIRED) {
    if (!values[field].trim()) {
      errors[field] = copy.requiredError;
    }
  }
  if (!errors.email && !EMAIL_PATTERN.test(values.email.trim())) {
    errors.email = copy.emailError;
  }

  return errors;
}

export default function ContactPage() {
  const t = useT();
  const copy = t.contact;

  usePageMeta(copy.metaTitle, copy.metaDescription);

  const profile = useApi('site-profile', fetchSiteProfile);
  const [values, setValues] = useState<LeadRequest>(() => emptyLead(copy.topics[0].value));
  const [errors, setErrors] = useState<Errors>({});
  const [status, setStatus] = useState<Status>('idle');

  function update(field: Field, value: string) {
    setValues((current) => ({ ...current, [field]: value }));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const found = validate(values, copy);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      return;
    }

    setStatus('submitting');
    try {
      await submitLead(values);
      setValues(emptyLead(copy.topics[0].value));
      setStatus('sent');
    } catch {
      setStatus('failed');
    }
  }

  function fieldClass(field: Field) {
    return errors[field] ? 'field has-error' : 'field';
  }

  return (
    <>
      <PageHero eyebrow={copy.heroEyebrow} title={copy.heroTitle} lead={copy.heroLead} />

      <section className="section contact">
        <div>
          <h2 style={{ fontSize: 'clamp(28px, 3vw, 40px)' }}>{copy.directTitle}</h2>
          {profile.status === 'ready' && (
            <div className="contact-box" style={{ marginTop: 28 }}>
              <div className="contact-row">
                <span>{copy.emailLabel}</span>
                <a href={`mailto:${profile.data.email}`}>{profile.data.email}</a>
              </div>
            </div>
          )}

          <div className="outcome" style={{ marginTop: 34 }}>
            <strong>{copy.outcomeTitle}</strong>
            <span>{copy.outcomeText}</span>
          </div>
        </div>

        <form className="form" onSubmit={handleSubmit} noValidate>
          <div className={fieldClass('name')}>
            <label htmlFor="name">{copy.nameLabel}</label>
            <input
              id="name"
              name="name"
              type="text"
              autoComplete="name"
              value={values.name}
              onChange={(event) => update('name', event.target.value)}
            />
            {errors.name && (
              <span className="error" role="alert">
                {errors.name}
              </span>
            )}
          </div>

          <div className="field-row">
            <div className={fieldClass('company')}>
              <label htmlFor="company">{copy.companyLabel}</label>
              <input
                id="company"
                name="company"
                type="text"
                autoComplete="organization"
                value={values.company}
                onChange={(event) => update('company', event.target.value)}
              />
              {errors.company && (
                <span className="error" role="alert">
                  {errors.company}
                </span>
              )}
            </div>
            <div className={fieldClass('role')}>
              <label htmlFor="role">{copy.roleLabel}</label>
              <input
                id="role"
                name="role"
                type="text"
                autoComplete="organization-title"
                value={values.role}
                onChange={(event) => update('role', event.target.value)}
              />
              {errors.role && (
                <span className="error" role="alert">
                  {errors.role}
                </span>
              )}
            </div>
          </div>

          <div className="field-row">
            <div className={fieldClass('email')}>
              <label htmlFor="email">{copy.emailLabel}</label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                value={values.email}
                onChange={(event) => update('email', event.target.value)}
              />
              {errors.email && (
                <span className="error" role="alert">
                  {errors.email}
                </span>
              )}
            </div>
            <div className="field">
              <label htmlFor="phone">
                {copy.phoneLabel} <span className="hint">{copy.phoneHint}</span>
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                autoComplete="tel"
                value={values.phone}
                onChange={(event) => update('phone', event.target.value)}
              />
            </div>
          </div>

          <div className={fieldClass('topic')}>
            <label htmlFor="topic">{copy.topicLabel}</label>
            <select
              id="topic"
              name="topic"
              value={values.topic}
              onChange={(event) => update('topic', event.target.value)}
            >
              {copy.topics.map((topic) => (
                <option key={topic.value} value={topic.value}>
                  {topic.label}
                </option>
              ))}
            </select>
          </div>

          <div className={fieldClass('message')}>
            <label htmlFor="message">{copy.messageLabel}</label>
            <textarea
              id="message"
              name="message"
              placeholder={copy.messagePlaceholder}
              value={values.message}
              onChange={(event) => update('message', event.target.value)}
            />
            {errors.message && (
              <span className="error" role="alert">
                {errors.message}
              </span>
            )}
          </div>

          {/* Spam-fälla: riktiga användare ser aldrig fältet. */}
          <div className="honeypot" aria-hidden="true">
            <label htmlFor="company_website">{copy.honeypotLabel}</label>
            <input
              id="company_website"
              name="company_website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={values.companyWebsite}
              onChange={(event) => update('companyWebsite', event.target.value)}
            />
          </div>

          <button className="button primary" type="submit" disabled={status === 'submitting'}>
            {status === 'submitting' ? copy.submitting : copy.submit}
          </button>
          <p className="form-note">{copy.formNote}</p>

          {status === 'sent' && (
            <p className="form-status" role="status">
              {copy.sent}
            </p>
          )}
          {status === 'failed' && (
            <p className="form-status is-error" role="alert">
              {copy.failed}
            </p>
          )}
        </form>
      </section>
    </>
  );
}
